import React from 'react';
import { useIntl } from 'react-intl';
import Link from '@mui/material/Link';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import Tooltip from '@/components/mui/Tooltip';
import {
  AvatarWrapper,
  ChatHeaderActionButton,
  ChatHeaderChannelName,
  ChatHeaderCompanyName,
  ChatHeaderLeft,
  ChatHeaderNameRow,
  ChatHeaderRight,
  ChatHeaderRoot,
  ChatHeaderTitleArea,
} from '@/components/mui/Layouts/components/Chat/components/ChatPanel/StyledComponents';

export interface ChatHeaderViewProps {
  channelName: string;
  /** Avatar node (Sendbird ChannelAvatar in the app, MUI Avatar in showcases). */
  avatar: React.ReactNode;
  /** Company name row — rendered only when provided (multi-company users with companyId in context). */
  companyName?: string | null;
  /** When set (AP channels), the company name renders as a link to the company workspace. */
  companyHref?: string | null;
  /** Eye icon — rendered only when the channel context has a documentId. */
  onPreviewDocument?: (() => void) | null;
  /** Open-in-new link — rendered only when context differs from the current page. */
  openInNewTabHref?: string | null;
  /**
   * Mobile view flag. In mobile view the app is unsupported in a second browser
   * tab (opening one lands on the "Browser is not supported" page), so the
   * company link renders as plain text and the open-in-new-tab button is hidden.
   */
  isMobile?: boolean;
  onToggleSettings: () => void;
  /**
   * Whether to show the "Chat information" button that opens the settings panel.
   * Hidden for the frozen Welcome channel — a read-only system chat with no
   * editable settings (no rename, picture change, or member add/remove). DA-19521.
   */
  showSettings?: boolean;
  onClose: () => void;
  onBackToList?: () => void;
}

// The Tooltip Popper defaults to theme.zIndex.tooltip (1500), but the chat
// panel is a MUI Drawer modal stacked at zIndex 1600 (see chat modal fixes),
// so the header tooltips render behind the drawer and are invisible. Lift the
// Popper above the drawer (matches the 1700 used by the chat dialogs/preview
// modal).
const headerTooltipSlotProps = { popper: { sx: { zIndex: 'chatPopup' } } } as const;

const ChatHeaderView: React.FC<ChatHeaderViewProps> = ({
  channelName,
  avatar,
  companyName,
  companyHref,
  onPreviewDocument,
  openInNewTabHref,
  isMobile = false,
  onToggleSettings,
  showSettings = true,
  onClose,
  onBackToList,
}) => {
  const { formatMessage } = useIntl();

  return (
    <ChatHeaderRoot>
      <ChatHeaderLeft>
        {onBackToList && (
          <ChatHeaderActionButton onClick={onBackToList}>
            <ArrowBackIcon />
          </ChatHeaderActionButton>
        )}
        <AvatarWrapper>{avatar}</AvatarWrapper>
        <ChatHeaderTitleArea>
          <ChatHeaderNameRow>
            <Tooltip title={channelName} placement="bottom" disableInteractive slotProps={headerTooltipSlotProps}>
              <ChatHeaderChannelName>{channelName}</ChatHeaderChannelName>
            </Tooltip>
          </ChatHeaderNameRow>
          {companyName && (
            <ChatHeaderCompanyName variant="subtitle2">
              {companyHref && !isMobile ? (
                <Link href={companyHref} target="_blank" rel="noopener noreferrer" underline="always" color="inherit">
                  {companyName}
                </Link>
              ) : (
                companyName
              )}
            </ChatHeaderCompanyName>
          )}
        </ChatHeaderTitleArea>
      </ChatHeaderLeft>
      <ChatHeaderRight>
        {onPreviewDocument && (
          <Tooltip
            title={formatMessage({ id: 'chat.previewDocument', defaultMessage: 'Preview document' })}
            disableInteractive
            slotProps={headerTooltipSlotProps}
          >
            <ChatHeaderActionButton onClick={onPreviewDocument}>
              <VisibilityOutlinedIcon />
            </ChatHeaderActionButton>
          </Tooltip>
        )}
        {showSettings && (
          <Tooltip
            title={formatMessage({ id: 'chat.header.chatInformation', defaultMessage: 'Chat information' })}
            disableInteractive
            slotProps={headerTooltipSlotProps}
          >
            <ChatHeaderActionButton onClick={onToggleSettings}>
              <InfoOutlinedIcon />
            </ChatHeaderActionButton>
          </Tooltip>
        )}
        {openInNewTabHref && !isMobile && (
          <Tooltip
            title={formatMessage({ id: 'chat.header.openInNewTab', defaultMessage: 'Open in new tab' })}
            disableInteractive
            slotProps={headerTooltipSlotProps}
          >
            <ChatHeaderActionButton component="a" href={openInNewTabHref} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon />
            </ChatHeaderActionButton>
          </Tooltip>
        )}
        <ChatHeaderActionButton onClick={onClose}>
          <CloseIcon />
        </ChatHeaderActionButton>
      </ChatHeaderRight>
    </ChatHeaderRoot>
  );
};

export default ChatHeaderView;
